/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SparkLine';
import { toOptions } from 'componentregistry/utils/ManagerUtils';

const SparkLineManager: ComponentManagerFn<'SparkLine'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SparkLine'>(platform, 'SparkLine'),
    type: 'SparkLine',
    name: 'Spark Line',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      // 🔹 Data
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      valueAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => {
          return ['Integer', 'Decimal', 'Double', 'Percent'];
        },
        label: 'Value Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'It is a column through which you can get the data from DataSource.',
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
      },
      // 🔹 General
      chartWidth: {
        category: 'General',
        label: 'Chart Width',
        type: 'number',
        max: 1000,
      },
      chartHeight: {
        category: 'General',
        label: 'Chart Height',
        type: 'number',
        max: 1000,
      },

      // 🔹 Caption & Sub-Caption
      caption: {
        category: 'General',
        label: 'Caption',
        type: 'expression',
        returnType: async () => 'string',
      },
      captionFont: {
        category: 'General',
        label: 'Caption Font',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Font family for the chart caption.',
      },
      captionFontSize: {
        category: 'General',
        label: 'Caption Font Size',
        type: 'number',
        helpText: 'Font size for the chart caption.',
      },
      captionFontColor: {
        category: 'General',
        label: 'Caption Font Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for the chart caption text.',
      },
      captionFontBold: {
        category: 'General',
        label: 'Caption Font Bold',
        type: 'boolean',
        helpText: 'Make caption text bold.',
      },
      subCaption: {
        category: 'General',
        label: 'Sub-Caption',
        type: 'expression',
        returnType: async () => 'string',
      },
      subCaptionFont: {
        category: 'General',
        label: 'Sub-Caption Font',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Font family for the chart sub-caption.',
      },
      subcaptionFontSize: {
        category: 'General',
        label: 'Sub-Caption Font Size',
        type: 'number',
        helpText: 'Font size for the chart sub-caption.',
      },
      subcaptionFontColor: {
        category: 'General',
        label: 'Sub-Caption Font Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for the sub-caption text.',
      },
      subcaptionFontBold: {
        category: 'General',
        label: 'Sub-Caption Font Bold',
        type: 'boolean',
        helpText: 'Make sub-caption text bold.',
      },
      bgColor: {
        category: 'General',
        label: 'Background Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify the hex code for Background color for the chart.',
      },

      // 🔹 Period
      periodlength: {
        category: 'Period',
        label: 'Period Length',
        type: 'number',
        helpText: 'Length of each period in the sparkline.',
      },
      periodcolor: {
        category: 'Period',
        label: 'Period Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for period divisions.',
      },
      periodalpha: {
        category: 'Period',
        label: 'Period Alpha',
        type: 'number',
        helpText: 'Transparency level for period divisions (0-100).',
      },

      // 🔹 Anchors
      drawAnchor: {
        category: 'Data Points',
        label: 'Show Anchors',
        type: 'select',
        options: toOptions(['Hide All', 'Show All', 'Default']),
        helpText: 'Show/hide anchors on data points.',
      },
      showOpenAnchor: {
        category: 'Data Points',
        label: 'Show Open Anchor',
        type: 'boolean',
        helpText: 'Show/hide open value anchors.',
      },
      showCloseAnchor: {
        category: 'Data Points',
        label: 'Show Close Anchor',
        type: 'boolean',
        helpText: 'Show/hide close value anchors.',
      },
      showHighAnchor: {
        category: 'Data Points',
        label: 'Show High Anchor',
        type: 'boolean',
        helpText: 'Show/hide high value anchors.',
      },
      showLowAnchor: {
        category: 'Data Points',
        label: 'Show Low Anchor',
        type: 'boolean',
        helpText: 'Show/hide low value anchors.',
      },
      // 🔹 Line
      lineColor: {
        category: 'Line',
        label: 'Line Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for the sparkline.',
      },
      lineThickness: {
        category: 'Line',
        label: 'Line Thickness',
        type: 'number',
        helpText: 'Thickness of the sparkline.',
      },
      lineAlpha: {
        category: 'Line',
        label: 'Line Alpha',
        type: 'number',
        helpText: 'Transparency level for the line (0-100).',
      },

      // 🔹 Data Points
      anchorRadius: {
        category: 'Data Points',
        label: 'Anchor Radius',
        type: 'number',
        helpText: 'Radius of anchor points.',
      },
      anchorAlpha: {
        category: 'Data Points',
        label: 'Anchor Alpha',
        type: 'number',
        helpText: 'Transparency level for anchors (0-100).',
      },

      // 🔹 Interactivity
      showTooltip: {
        category: 'Interactivity',
        label: 'Show Tooltip',
        type: 'boolean',
        helpText: 'Show/hide tooltips on hover.',
      },
      plotToolText: {
        category: 'Interactivity',
        label: 'Plot Tool Text',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Custom tooltip text format.',
      },
      //trendlines
      showTrendLine: {
        category: 'Trend Lines',
        label: 'Show Trend Line',
        type: 'boolean',
        helpText: 'Turn this on to show Trend Line',
      },
      startValue: {
        category: 'Trend Lines',
        label: 'Start Value',
        type: 'number',
        helpText: 'Starting point for the trendline',
      },
      endValue: {
        category: 'Trend Lines',
        label: 'End Value',
        type: 'number',
        helpText: 'End point for the trendline',
      },
      thickness: {
        category: 'Trend Lines',
        label: 'Thickenss',
        type: 'number',
        helpText: 'Thickness for the trendline',
      },
      alpha: {
        category: 'Trend Lines',
        label: 'Alpha',
        type: 'number',
        helpText: 'Transparency level for Trendline (0-100).',
      },
      color: {
        category: 'Trend Lines',
        label: 'Trend Line Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for the trendline.',
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' }
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    isPropertyVisible(prop, itemProps): boolean {
      switch (prop as string) {
        case 'plotToolText':
          return itemProps.showTooltip;
        case 'startValue':
        case 'endValue':
        case 'alpha':
        case 'color':
        case 'thickness':
          return itemProps.showTrendLine;
        case 'valueAttribute': // Add this line
          return itemProps.dsalias !== '';
        default:
          return true;
      }
    },
    category: 'Fusion Charts',
    icon: { iconName: 'chart-mixed' },
    description:
      'Sparklines are compact charts that provide a simple, word-sized visual representation of data trends or variations over time, without axes or detailed labels',
  };
};
export default SparkLineManager;
